import React from "react";
import { Edit2 } from "lucide-react";
import { useTranslation } from "../../../contexts/I18nProvider";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDispatch } from "react-redux";
import { setEditMode } from "../../../redux/slices/profileSlice";
import styles from "./ProfileHero.module.css";

const ProfileHero = ({ user, loading }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const dispatch = useDispatch();

  const handleEditClick = () => {
    dispatch(setEditMode(true));
  };

  // Format last login date
  const formatLastLogin = (date) => {
    if (!date) return t("pages.profile.never");
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return t("pages.profile.justNow");
    if (diffHours < 24) return `${diffHours}h ${t("pages.profile.ago")}`;
    if (diffDays < 7) return `${diffDays}d ${t("pages.profile.ago")}`;

    return then.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: diffDays > 365 ? "numeric" : undefined,
    });
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (!user?.firstName || !user?.lastName) return "U";
    return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
  };

  return (
    <div className={`${styles.hero} ${isDark ? styles.dark : ""}`}>
      {loading ? (
        // Skeleton loading state
        <div className={styles.heroContent}>
          <div className={`${styles.avatar} ${styles.skeleton}`} />
          <div className={styles.info}>
            <div className={`${styles.name} ${styles.skeleton}`} />
            <div className={`${styles.subtitle} ${styles.skeleton}`} />
          </div>
        </div>
      ) : (
        // Actual content
        <>
          <div className={styles.heroContent}>
            {/* Avatar */}
            <div className={styles.avatar}>
              {user?.avatar ? (
                <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
              ) : (
                <div className={styles.initials}>{getInitials()}</div>
              )}
            </div>

            {/* User Info */}
            <div className={styles.info}>
              <h1 className={styles.name}>
                {user?.firstName} {user?.lastName}
              </h1>
              <p className={styles.email}>{user?.email}</p>
            </div>
          </div>

          {/* Edit Button */}
          <button
            onClick={handleEditClick}
            className={styles.editButton}
            aria-label={t("pages.profile.editProfile")}
            title={t("pages.profile.editProfile")}
          >
            <Edit2 size={20} />
            <span>{t("pages.profile.edit")}</span>
          </button>
        </>
      )}
    </div>
  );
};

export default ProfileHero;
